import { Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Route } from 'react-router-dom';
import { Dialog } from 'vet-bones/components';
import { noop } from 'vet-bones/utils';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectVirtualGraphToCreate } from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectVirtualGraphPanel } from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceNativeQuoteCharacterSelector } from 'src/selectors/dataSourceNativeQuoteCharacter';
import { dataSourceSelector } from 'src/selectors/dataSourceSelector';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const ProjectVirtualGraphPreviewDialogBody: React.VFC = () => {
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();

  const { resourceId } = useReduxSelector(
    (state) => state.ui.virtualGraphPreviewDialog
  );
  const virtualGraph = useReduxSelector(
    projectVirtualGraphSelector(projectId, resourceId)
  );
  const dataSource = useReduxSelector(
    dataSourceSelector(virtualGraph?.dataSourceId)
  );
  const connection = useReduxSelector(connectionSelector);
  const nativeQuoteCharacter = useReduxSelector(
    dataSourceNativeQuoteCharacterSelector(
      virtualGraph?.dataSourceType || null,
      connection
    )
  );

  const handleSetContents = React.useCallback(
    (id: string, update: Partial<ProjectVirtualGraphToCreate>) => {
      if (update.fields?.length && update.previewRows?.length && connection) {
        dispatch(
          projectsActionCreators.updatedProjectVirtualGraphPreview(
            projectId,
            id,
            update.fields,
            update.previewRows,
            connection.id
          )
        );
      }
    },
    [dispatch, projectId, connection]
  );

  return virtualGraph ? (
    <ProjectVirtualGraphPanel
      dataSource={dataSource}
      disableDataSourceSidebar
      disableEditing
      enablePreviewRefresh
      existingVirtualGraph={virtualGraph}
      handleUpdateVirtualGraph={handleSetContents}
      isUsingCustomQuery={typeof virtualGraph.from === 'string'}
      nativeQuoteCharacter={nativeQuoteCharacter}
      selectedTab={virtualGraph.id}
      setErrorText={noop}
      setIsUsingCustomQuery={noop}
      setSelectedTab={noop}
      setVirtualGraphOptions={noop}
      tables={[]}
      updateVirtualGraphOptions={noop}
      virtualGraphOptions={virtualGraph.options}
      virtualGraphsToCreate={{ [virtualGraph.id]: virtualGraph }}
    />
  ) : null;
};

export const ProjectVirtualGraphPreviewDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();

  const { isOpen } = useReduxSelector(
    (state) => state.ui.virtualGraphPreviewDialog
  );

  const handleToggleDialog = React.useCallback(
    (isOpen: boolean) =>
      dispatch(uiSlice.actions.toggledVirtualGraphPreviewDialog({ isOpen })),
    [dispatch]
  );

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className={classNames('sd-dialog-project-resource', 'sd-dialog-large')}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={() => handleToggleDialog(false)}
      title={copy.components.virtualGraphPreviewDialog.title}
    >
      <div className={Classes.DIALOG_BODY}>
        <Route path={ROUTE.PROJECT}>
          <ProjectVirtualGraphPreviewDialogBody />
        </Route>
      </div>
    </Dialog>
  );
};
